import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { format } from "date-fns";
import type { User } from "@supabase/supabase-js";
import * as orderService from "@/supabase/order/service/orderService";
import { toast } from "sonner";
import { logError } from "@/lib/sentry";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

interface UseMoveOrderDateProps {
  user: User | null;
  selectedDate: Date;
  activeCustomer: CustomerTemplate | null | undefined;
  activeCustomerId: string | null;
  selectedCustomerIds: string[];
  setSelectedCustomerIds: (ids: string[]) => void;
  isBatchProcessing: boolean;
  setIsBatchProcessing: (value: boolean) => void;
  extractOrderId: (customerId: string) => number | null;
  refreshOrders: () => Promise<void>;
}

interface UseMoveOrderDateResult {
  isMoveOrderDateDialogOpen: boolean;
  moveOrderDate: Date;
  moveDateMode: 'single' | 'bulk';
  setMoveOrderDate: Dispatch<SetStateAction<Date>>;
  openMoveOrderDateDialog: (modeOrDate?: 'single' | 'bulk' | Date, presetDate?: Date) => void;
  handleMoveOrderDateOpenChange: (open: boolean) => void;
  handleConfirmMoveOrderDate: () => Promise<void>;
}

/**
 * 주문 날짜 변경(단건/일괄) 도메인 훅.
 * 다이얼로그 open state·선택 날짜·모드 + parsed_order 동기화 헬퍼 + 확정 로직을 응집.
 * extractOrderId / selectedCustomerIds / isBatchProcessing 등 공유 hub는 Home 소유 →
 * live 참조로 주입(snapshot 금지). parsed_order.order_date == order.order_date 불변식 보존.
 */
export function useMoveOrderDate({
  user,
  selectedDate,
  activeCustomer,
  activeCustomerId,
  selectedCustomerIds,
  setSelectedCustomerIds,
  isBatchProcessing,
  setIsBatchProcessing,
  extractOrderId,
  refreshOrders,
}: UseMoveOrderDateProps): UseMoveOrderDateResult {
  const [isMoveOrderDateDialogOpen, setIsMoveOrderDateDialogOpen] = useState(false);
  const [moveOrderDate, setMoveOrderDate] = useState<Date>(new Date());
  const [moveDateMode, setMoveDateMode] = useState<'single' | 'bulk'>('single');

  const openMoveOrderDateDialog = useCallback((modeOrDate?: 'single' | 'bulk' | Date, presetDate?: Date) => {
    const mode: 'single' | 'bulk' = (modeOrDate === 'single' || modeOrDate === 'bulk') ? modeOrDate : 'single';
    const initial = modeOrDate instanceof Date ? modeOrDate : presetDate;
    setMoveDateMode(mode);
    setMoveOrderDate(initial ?? (selectedDate || new Date()));
    setIsMoveOrderDateDialogOpen(true);
  }, [selectedDate]);

  const handleMoveOrderDateOpenChange = useCallback((open: boolean) => {
    if (!open && isBatchProcessing) return; // 처리 중 외부 클릭/ESC 가드
    setIsMoveOrderDateDialogOpen(open);
  }, [isBatchProcessing]);

  // parsed_order 동기화 helper (single): 1회 자동 재시도. { ok, updated } 반환.
  const trySyncSingleParsed = useCallback(async (orderId: number, target: string): Promise<{ ok: boolean; updated: boolean }> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { updateParsedOrderDateByOrderId } = await import('@/supabase/parsedorder/parsedOrderService');
        const updated = await updateParsedOrderDateByOrderId(orderId, target);
        return { ok: true, updated };
      } catch (err) {
        logError(`[Home.tsx] parsed_order 동기화 실패 (single, ${attempt === 0 ? '1차' : '재시도'})`, err as Error);
      }
    }
    return { ok: false, updated: false };
  }, []);

  // parsed_order 동기화 helper (bulk): 1회 자동 재시도. { ok, updatedCount } 반환.
  const trySyncBulkParsed = useCallback(async (orderIds: number[], target: string): Promise<{ ok: boolean; updatedCount: number }> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { updateParsedOrderDatesByOrderIds } = await import('@/supabase/parsedorder/parsedOrderService');
        const updatedCount = await updateParsedOrderDatesByOrderIds(orderIds, target);
        return { ok: true, updatedCount };
      } catch (err) {
        logError(`[Home.tsx] parsed_order 동기화 실패 (bulk, ${attempt === 0 ? '1차' : '재시도'})`, err as Error);
      }
    }
    return { ok: false, updatedCount: 0 };
  }, []);

  // 동기화 실패 알림: warning toast + 재시도 액션 (재시도 또 실패 시 재귀)
  const notifyParsedSyncFailed = useCallback((orderIds: number[], target: string) => {
    toast.warning("주문 날짜는 변경됐지만 배송 엑셀 동기화에 실패했습니다.", {
      duration: Infinity,
      action: {
        label: "재시도",
        onClick: async () => {
          const { ok } = await trySyncBulkParsed(orderIds, target);
          if (ok) {
            toast.success("배송 엑셀 동기화 완료");
          } else {
            notifyParsedSyncFailed(orderIds, target);
          }
        },
      },
    });
  }, [trySyncBulkParsed]);

  const handleConfirmMoveOrderDate = useCallback(async () => {
    const target = format(moveOrderDate, "yyyy-MM-dd");

    if (moveDateMode === 'bulk') {
      if (!user) {
        toast.error("로그인이 필요한 기능입니다.");
        return;
      }
      if (selectedCustomerIds.length === 0) {
        toast.info("선택된 주문이 없습니다.");
        setIsMoveOrderDateDialogOpen(false);
        return;
      }
      const orderIds = selectedCustomerIds
        .map(id => extractOrderId(id))
        .filter((id): id is number => id !== null);
      if (orderIds.length === 0) {
        toast.error("유효한 주문이 없습니다.");
        return;
      }
      setIsBatchProcessing(true);
      try {
        await orderService.updateOrdersDate(orderIds, target);
        const { ok, updatedCount } = await trySyncBulkParsed(orderIds, target);
        const bulkExtra = (ok && updatedCount > 0) ? ' (배송 엑셀의 날짜도 함께 변경되었습니다)' : '';
        toast.success(`총 ${orderIds.length}건의 주문 날짜를 ${target}로 변경했습니다.${bulkExtra}`, { duration: 5000 });
        if (!ok) notifyParsedSyncFailed(orderIds, target);
        setSelectedCustomerIds([]);
        setIsMoveOrderDateDialogOpen(false);
        await refreshOrders();
      } catch (error) {
        logError("주문 날짜 일괄 이동 실패", error as Error);
        toast.error("주문 날짜 일괄 이동에 실패했습니다. 다시 시도해 주세요.");
      } finally {
        setIsBatchProcessing(false);
      }
      return;
    }

    // single
    if (!activeCustomer || !activeCustomerId) {
      setIsMoveOrderDateDialogOpen(false);
      return;
    }
    const orderId = extractOrderId(activeCustomerId);
    if (!orderId) {
      toast.error("유효하지 않은 주문 ID입니다.");
      return;
    }
    try {
      await orderService.updateOrderDate(orderId, target);
      const { ok, updated } = await trySyncSingleParsed(orderId, target);
      const baseMsg = `'${activeCustomer.nickname}'님의 주문 날짜를 ${target}로 이동했습니다.`;
      const extra = (ok && updated) ? ' (배송 엑셀의 날짜도 함께 변경되었습니다)' : '';
      toast.success(baseMsg + extra);
      if (!ok) notifyParsedSyncFailed([orderId], target);
      setIsMoveOrderDateDialogOpen(false);
      await refreshOrders();
    } catch (error) {
      logError("주문 날짜 이동 실패", error as Error);
      toast.error("주문 날짜 이동에 실패했습니다. 다시 시도해 주세요.");
    }
  }, [
    activeCustomer, activeCustomerId, moveOrderDate, moveDateMode,
    extractOrderId, refreshOrders,
    user, selectedCustomerIds, setSelectedCustomerIds, setIsBatchProcessing,
    trySyncSingleParsed, trySyncBulkParsed, notifyParsedSyncFailed,
  ]);

  return {
    isMoveOrderDateDialogOpen,
    moveOrderDate,
    moveDateMode,
    setMoveOrderDate,
    openMoveOrderDateDialog,
    handleMoveOrderDateOpenChange,
    handleConfirmMoveOrderDate,
  };
}
